import React, { useState, useEffect, useContext } from "react";
import { Icon } from '@iconify/react/dist/iconify.js';
import axios from 'axios'; 
import { toast, ToastContainer } from 'react-toastify'; 
import 'react-toastify/dist/ReactToastify.css'; 
import { useNavigate } from "react-router-dom";
import ComplaintUploadWithImagePreviewList from "../components/child/ComplaintUploadWithImagePreviewList"
import { LoginContext } from "../AuthContext/LoginContext";
import { getMediaUrl } from "../utils/uploadService";

// Stage 3 (Reply Submitted) SMS is always sent to these numbers
const SMS_REPLY_NOTIFY_NUMBERS = ['03454273073', '03379926560'];

const ComplainFormPageStatusUpdate = ({ complainId, probeMode = false }) => {
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;
  const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
  const authUserId = storedUser._id;
  const navigate = useNavigate();
  const { hasPermission } = useContext(LoginContext);

  // Only the officer the complaint is directly assigned to (assigned_to) may
  // change/close it. Everyone else in the communication loop or upper hierarchy
  // (e.g. the Conservator/CF above the assigned DCF) gets a read-only view.
  // Admins keep full control.
  const [assignedToId, setAssignedToId] = useState(null);

  const [formData, setFormData] = useState({
    dept_id: "",
    dept_name: "",
    district_id: "",
    complaint_district_name: "",
    complaint_number: "",
    complaint_nature_id: "",
    complaint_nature_name: "",
    complaint_nearby_place: "",
    complaint_detail: "",
    complainant_name: "",
    complainant_address: "",
    complainant_contact_no: "",
    complainant_email: "",
    complaint_time: "",
    complaint_additional_remarks: "",
    complainant_phone_secondry: "",
    other_nature: "",
    received_by: "",
    status: "",
    priority_level: "",
    cnic: "",
    complaint_source: "",
    complainant_tehsil: "",
    complainant_district: "",
    complaint_tehsil: "",
    complaint_district: "",
    complaint_address: "",
    status_change_reason: "",
    dfo_reply_media_array: [],
    media_array: [], // Added to store evidence URLs
    under_inquiry: null,
  });

  const [isLoading, setIsLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showEvidenceModal, setShowEvidenceModal] = useState(false); // State for modal visibility

  // Fetch complaint data to update
  useEffect(() => {
    const fetchComplaintData = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API_BASE_URL}/backend/complaints/${complainId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.status) {
          const complaintData = response.data.data;
          // Who is this complaint directly assigned to? Drives the Closed-option gate.
          // assigned_to may arrive populated ({_id,...}) or as a bare id string.
          const complaintAssignedToId =
            complaintData.assigned_to?._id || complaintData.assigned_to || null;

          setAssignedToId(complaintAssignedToId);
          setFormData({
            dept_id: complaintData.dept_id?._id || "",
            dept_name: complaintData.dept_id?.name || "",
            complaint_number: complaintData.complaint_number || "",
            district_id: complaintData.complaint_district_id?._id || "",
            complaint_nature_id: complaintData.complaint_nature_id?._id || "",
            complaint_nature_name: complaintData.complaint_nature_id?.name || "",
            complaint_nearby_place: complaintData.near_by_place || "",
            complaint_detail: complaintData.complaint_detail || "",
            complainant_name: complaintData.complainant_name || "",
            complainant_address: complaintData.complainant_address || "",
            complainant_contact_no: complaintData.complainant_contact_no || "",
            complainant_email: complaintData.complainant_email || "",
            complaint_time: complaintData.time_of_call || "",
            complaint_additional_remarks: complaintData.remarks || "",
            complainant_phone_secondry: complaintData.complainant_contact_no_sec || "",
            other_nature: complaintData.other_nature || "",
            received_by: complaintData.received_by?.first_name + ' ' + complaintData.received_by?.last_name || "",
            // Status updates from both Assigned (In Progress) and Reopened
            // complaints submit a reply, whose persisted status is "Closed".
            // Keep this default even for read-only viewers opening the URL so
            // the dropdown never displays the current "Reopened" value.
            status: probeMode
              ? "Probe Reply Submitted"
              : ["In Progress", "Reopened"].includes(complaintData.status)
                ? "Closed"
                : complaintData.status || "Open",
            priority_level: complaintData.priority_level || "",
            cnic: complaintData.complainant_cnic || "",
            complaint_source: complaintData.complaint_source || "",
            complainant_tehsil: complaintData.complainant_tehsil_id?.name || "",
            complainant_district: complaintData.complainant_district_id?.name || "",
            complaint_tehsil: complaintData.complaint_tehsil_id?.name || "",
            complaint_district: complaintData.complaint_district_id?.name || "",
            complaint_address: complaintData.complaint_address || "",
            lat: complaintData.lat || "",
            long: complaintData.long || "",
            status_change_reason: "",
            dfo_reply_media_array: [],
            media_array: complaintData.media_array || [], // Store media_array from API
            under_inquiry: complaintData.under_inquiry || null,
            persons_in_loop: complaintData.persons_in_loop || [],
            current_status: complaintData.status || "",
          });
        }
      } catch (error) {
        toast.error("Failed to fetch complaint data.");
        console.error("Error fetching complaint data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComplaintData();
  }, [API_BASE_URL, complainId, authUserId, probeMode]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const getPriorityClasses = (priority) => {
    switch (priority) {
      case "Low":
        return "bg-info-focus text-info-600";
      case "Medium":
        return "bg-success-focus text-success-600";
      case "High":
        return "bg-warning-focus text-warning-600";
      case "Critical":
        return "bg-danger-focus text-danger-600";
      default:
        return "bg-secondary text-secondary-600";
    }
  };

  const handleSelectChange = (e) => {
    const { value, name } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleFileUpload = (uploadedUrl) => {
    console.log("Uploaded URL received:", uploadedUrl);
    setUploadedFiles(prevFiles => {
      const updatedFiles = [...prevFiles, uploadedUrl];
      console.log("Updated files array:", updatedFiles);
      return updatedFiles;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.status_change_reason) {
      toast.error("Please provide a status change reason");
      return;
    }

    if (probeMode && uploadedFiles.length === 0) {
      toast.error("Please attach supporting evidence for the probe reply");
      return;
    }

    console.log("Uploaded Files:", uploadedFiles);

    const apiData = probeMode
      ? {
          id: complainId,
          status: "Probe Reply Submitted",
          user_remarks: formData.status_change_reason,
          user_media_array: uploadedFiles,
        }
      : {
          id: complainId,
          status: formData.status,
          dfo_remarks: formData.status_change_reason,
          dfo_reply_media_array: uploadedFiles,
        };

    console.log("API Payload:", apiData);

    try {
      const token = localStorage.getItem("token");
      const endpoint = probeMode ? 'change-status' : 'dfo-reply';
      const response = await axios.post(`${API_BASE_URL}/backend/complaints/${endpoint}`, apiData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.data.status) {
        if (formData.status === "Closed") {
          // Stage 3 — Officer Reply Submitted: SMS to officers in loop + fixed numbers
          try {
            const officerName = `${storedUser?.first_name || ''} ${storedUser?.last_name || ''}`.trim() || 'Officer';
            const replySms = `Dear Sir, the ${officerName} has submitted a reply on complaint ${formData.complaint_number} ('${formData.complaint_nature_name}').`;
            const replyPhones = new Set([
              ...(formData.persons_in_loop || []).map(p => p.phone_no).filter(Boolean),
              ...SMS_REPLY_NOTIFY_NUMBERS,
            ]);
            if (storedUser?.phone_no && !SMS_REPLY_NOTIFY_NUMBERS.includes(storedUser.phone_no)) {
              replyPhones.delete(storedUser.phone_no);
            }

            await Promise.all(Array.from(replyPhones).map(phone =>
              axios.post('https://smsgateway.pitb.gov.pk/api/send_sms', null, {
                params: {
                  sec_key: process.env.REACT_APP_PITB_KEY,
                  sms_text: replySms,
                  phone_no: phone,
                  sms_language: 'english'
                }
              })
            ));
          } catch (smsError) {
            console.error("Error sending SMS:", smsError);
          }
        }

        // Status change: SMS to admin (03379926560)
        try {
          const statusSms = `Dear Sir, complaint ${formData.complaint_number} ('${formData.complaint_nature_name}') status has been changed from ${formData.current_status} to ${formData.status === 'Closed' ? 'Closed (Reply Submitted)' : formData.status}.`;
          await axios.post('https://smsgateway.pitb.gov.pk/api/send_sms', null, {
            params: {
              sec_key: process.env.REACT_APP_PITB_KEY,
              sms_text: statusSms,
              phone_no: "03379926560",
              sms_language: 'english'
            }
          });
        } catch (smsError) {
          console.error("Error sending SMS:", smsError);
        }

        toast.success("Complaint status updated successfully");
        setTimeout(() => {
          navigate(probeMode ? "/probe/list" : "/complains/list");
        }, 2000);
      } else {
        toast.error(response.data.message || "Failed to update complaint status");
      }
    } catch (error) {
      toast.error("Failed to update complaint status. Please try again.");
      console.error("Error updating complaint status:", error);
    }
  };

  // Function to open evidence link in a new window
  const openEvidenceLink = (url) => {
    window.open(url, '_blank');
  };

  // Function to toggle modal visibility
  const toggleEvidenceModal = () => {
    setShowEvidenceModal(!showEvidenceModal);
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // STRICT: only the exact account the complaint is directly assigned to may pick
  // "Closed". No admin/role override — upper hierarchy (CF), communication-loop
  // viewers, and admins cannot close a complaint that isn't tagged to them.
  const isAssignedOfficer = !!assignedToId && String(assignedToId) === String(authUserId);
  const canChangeStatus = isAssignedOfficer;

  return (
    <div className="col-lg-12 col-md-12 col-sm-12">
      <ToastContainer />
      
      {/* Header Section */}
      <div className="text-center">
        <h6 className="mb-20 text-2xl font-bold text-gray-800 flex items-center justify-center">
          <Icon icon="mdi:file-document-alert" className="text-3xl mx-3 my-1" />
          {probeMode ? 'Submit Probe Reply' : 'Update Complaint Status'}
        </h6>
      </div>
      
      {/* Main Card */}
      <div className="card-body py-10">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            <div className="shadow-4 border radius-8">
              
              {/* Complaint Summary Section */}
              <div className="p-20 border-bottom fs-5">
                <div className="row justify-content-between g-3">
                  {/* Left Column */}
                  <div className="col-sm-6">
                    
                    <h5 className="my-20">{formData.complaint_number} 
                      <span className={`px-3 py-1 rounded-pill fw-semibold text-xl ${getPriorityClasses(formData.priority_level)}`}>
                        {formData.priority_level || 'No Data Found'}
                      </span>
                    </h5>
                    
                    <div className="space-y-2">
                      {[
                        { label: 'Complaint Time', icon: 'mdi:clock-outline', value: formData.complaint_time },
                        { label: 'Department', icon: 'mdi:office-building', value: formData.dept_name },
                        {
                          label: 'Source of Complaint',
                          icon: 'mdi:source-branch',
                          value: (
                            <div className="d-flex align-items-center">
                              <span>{formData.complaint_source || 'N/A'}</span>
                              {formData.media_array && formData.media_array.length > 0 && (
                                <button
                                  className="btn btn-sm btn-success-600 mx-20"
                                  onClick={toggleEvidenceModal}
                                >
                                  Show Evidence
                                </button>
                              )}
                            </div>
                          )
                        },
                        {
                          label: 'Nature of Complaint',
                          icon: 'mdi:alert-circle',
                          value: (
                            <>
                              {formData.complaint_nature_name}
                              {formData.complaint_nature_name === 'Other' && formData.other_nature && (
                                <span className="ms-2 text-muted fst-italic">({formData.other_nature})</span>
                              )}
                            </>
                          )
                        },
                      ].map(({ label, icon, value }, index) => (
                        <div key={index} className="d-flex justify-content-between">
                          <span className="text-success d-flex align-items-center gap-1 col-4 fw-bold">
                            <Icon icon={icon} /> {label}
                          </span>
                          <span className="col-1">:</span>
                          <span className="col-7 text-decoration-underline">
                            {value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="col-sm-6 space-y-2">
                    {[
                      { label: 'Complaint Nearby Place', icon: 'mdi:map-marker', value: formData.complaint_nearby_place },
                      { label: 'Complaint Tehsil', icon: 'mdi:map', value: formData.complaint_tehsil },
                      { label: 'Complaint District', icon: 'mdi:map-outline', value: formData.complaint_district },
                      { label: 'Complaint Address', icon: 'mdi:home-map-marker', value: formData.complaint_address },
                      { label: 'Received By', icon: 'mdi:account', value: formData.received_by },
                    ].map(({ label, icon, value }, index) => (
                        <div key={index} className="d-flex mb-2">
                          <div className="col-4 fw-bold text-success d-flex align-items-center gap-1">
                            <Icon icon={icon} /> {label}
                          </div>
                          <div className="col-1 text-center">:</div>
                          <div className="col-7 text-decoration-underline">
                            {value || 'N/A'}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              <div className="py-28 px-20">
                <div className="row fs-5"> 
                  <div className="col-12 mt-3">
                        <h6 className="mb-3 text-success d-flex align-items-center gap-1 col-4 fw-bold"><Icon icon="mdi:clipboard-text" />Complaint Details</h6>
                        <p>{formData.complaint_detail || 'No Data Found'}</p>
                  </div>
                </div>
              </div>
               <hr/>
               <div className="py-28 px-20">
                <div className="row fs-5"> 
                  <div className="col-12 mt-3">
                        <h6 className="mb-3 text-success d-flex align-items-center gap-1 col-4 fw-bold"><Icon icon="mdi:text" />Additional Remarks</h6>
                        <p>{formData.complaint_additional_remarks || 'No Data Found'}</p>
                  </div>
                </div>
              </div>
              <hr/>
              {probeMode && formData.under_inquiry && (
                <>
                  <div className="py-28 px-20">
                    <h6 className="mb-3 text-warning d-flex align-items-center gap-1 fw-bold">
                      <Icon icon="mdi:magnify-scan" /> Probe Instructions
                    </h6>
                    <p>{formData.under_inquiry.description || 'No description provided'}</p>
                    {formData.under_inquiry.remarks && (
                      <p className="mb-0"><strong>Remarks:</strong> {formData.under_inquiry.remarks}</p>
                    )}
                    {formData.under_inquiry.number_of_days && (
                      <p className="mb-0"><strong>No. of Days for resolving the Probe:</strong> {formData.under_inquiry.number_of_days}</p>
                    )}
                    {formData.under_inquiry.media_array?.length > 0 && (
                      <div className="mt-3 d-flex flex-wrap gap-2">
                        {formData.under_inquiry.media_array.map((url, index) => (
                          <button
                            key={`${url}-${index}`}
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => openEvidenceLink(getMediaUrl(url))}
                          >
                            View Probe Document {index + 1}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <hr/>
                </>
              )}
              {/* Complainant Information Section */}
              <div className="py-28 px-20">
                <div className="row fs-5">
                  {/* Complainant Information (Left Column) */}
                  <div className="col-md-6">
                    <h6 className="text-md mb-3">Complainant Information:</h6>
                    <div className="space-y-2">
                      {[
                        { label: 'Name', icon: 'mdi:account', value: formData.complainant_name },
                        { label: 'Phone Number', icon: 'mdi:phone', value: formData.complainant_contact_no },
                        { label: 'Email', icon: 'mdi:email', value: formData.complainant_email },
                        { label: 'Phone Secondary', icon: 'mdi:phone', value: formData.complainant_phone_secondry },
                        { label: 'CNIC', icon: 'mdi:id-card', value: formData.cnic },
                      ].map(({ label, icon, value }, index) => (
                        <div key={index} className="d-flex mb-2">
                          <div className="col-4 fw-bold text-success d-flex align-items-center gap-1">
                            <Icon icon={icon} /> {label}
                          </div>
                          <div className="col-1 text-center">:</div>
                          <div className="col-7 text-decoration-underline">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Complainant Address (Right Column) */}
                  <div className="col-md-6">
                    <h6 className="text-md mb-3">Complainant Address:</h6>
                    <div className="space-y-2">
                      {[
                        { label: 'Address', icon: 'mdi:home', value: formData.complainant_address },
                        { label: 'Tehsil', icon: 'mdi:map', value: formData.complainant_tehsil },
                        { label: 'District', icon: 'mdi:map', value: formData.complainant_district },
                      ].map(({ label, icon, value }, index) => (
                        <div key={index} className="d-flex mb-2">
                          <div className="col-4 fw-bold text-success d-flex align-items-center gap-1">
                            <Icon icon={icon} /> {label}
                          </div>
                          <div className="col-1 text-center">:</div>
                          <div className="col-7 text-decoration-underline">{value}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Status Update Form Section */}
                <div className="mt-24">
                  <div className="scroll-sm">
                    <form onSubmit={handleSubmit}>
                      <div>
                        <div className="mb-4">
                          <div className="text-lg font-semibold">{probeMode ? 'Submit Probe Reply' : 'Change Status'}</div>
                          <hr className="my-3" />
                          
                          <div className="row gy-3">
                            {/* Status */}
                            <div className="col-lg-4 col-md-6 col-sm-12">
                              <label className="form-label d-flex align-items-center gap-2">
                                <Icon icon="mdi:status" className="text-lg" /> Status
                              </label>
                              <select
                                className="form-select"
                                name="status"
                                value={formData.status}
                                onChange={handleSelectChange}
                                required
                              >
                                {probeMode ? (
                                  <option value="Probe Reply Submitted">Submit Probe Reply</option>
                                ) : <>
                                <option value="Open" disabled={!hasPermission('complaints', 'open')}>Open</option>
                                <option value="In Progress" disabled={!hasPermission('complaints', 'in_progress')}>In Progress</option>
                                <option value="Escalated" disabled={!hasPermission('complaints', 'escalated')}>Escalated</option>
                                <option value="Reopened" disabled={!hasPermission('complaints', 'reopened')}>Reopened</option>
                                {/* Closed can only be picked by the officer the complaint
                                    is directly assigned to (or an admin) — not by upper
                                    hierarchy / communication-loop viewers. */}
                                {/* Value stays "Closed" (stored in DB); label shown as "Reply Submitted". */}
                                <option value="Closed" disabled={!hasPermission('complaints', 'closed') || !canChangeStatus}>Reply Submitted</option>
                                <option value="Probe Reply Submitted" disabled>Probe Reply Submitted</option>
                                </>}
                                {/* "Cancelled" (Dropped) status retired — use Closed instead. */}
                              </select>
                            </div>

                            {/* Status Change Reason */}
                            <div className="col-lg-4 col-md-6 col-sm-12">
                              <label className="form-label d-flex align-items-center gap-2">
                                <Icon icon="mdi:file-document" className="text-lg" />
                                {probeMode ? 'Probe Reply' : 'Status Change Reason'}
                              </label>
                              <textarea
                                name="status_change_reason"
                                className="form-control"
                                rows={4}
                                placeholder={probeMode ? 'Enter your probe reply...' : 'Enter the reason for status change...'}
                                value={formData.status_change_reason}
                                onChange={handleInputChange}
                                required
                              />
                            </div>

                            {/* File Upload */}
                            <div className="col-lg-4 col-md-6 col-sm-12">
                              <label className="form-label d-flex align-items-center gap-2">
                                <Icon icon="mdi:file-upload" className="text-lg" />
                                Supporting Documents
                              </label>
                              <ComplaintUploadWithImagePreviewList onFileUploadSuccess={handleFileUpload} />
                              <label className="text-sm mt-1 mb-0">
                                Upload up to 5 supported files of PDF, Word or JPG format (Max 5 MB each)
                              </label>
                            </div>
                          </div>
                        </div>
                        
                        {/* Submit Button */}
                        <div className="d-flex justify-content-end">
                          <button
                            className="btn btn-success btn-sm px-6 py-12 radius-12 my-24 mx-10 col-lg-2 col-md-4 col-sm-12"
                            type="submit"
                          >
                            {probeMode ? 'Submit Probe Reply' : 'Update Status'}
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Evidence Modal */}
      {showEvidenceModal && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Evidence Links</h5>
                <button type="button" className="btn-close" onClick={toggleEvidenceModal}></button>
              </div>
              <div className="modal-body">
                {formData.media_array && formData.media_array.length > 0 ? (
                  <ul className="list-group">
                    {formData.media_array.map((evidence, index) => (
                      <li
                        key={index}
                        className="list-group-item list-group-item-action"
                        style={{ cursor: 'pointer' }}
                        onClick={() => openEvidenceLink(getMediaUrl(evidence))}
                      >
                        Evidence-{index + 1}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No evidence available.</p>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={toggleEvidenceModal}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComplainFormPageStatusUpdate;
